import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import cameraIcon from "../../Components/Assets/images/camera-switch-front-back.svg";
import { compressImage } from "../../utility/compressImage";

const WebcamDialogStudent = ({ open, onClose, imageBase64, onSave }) => {
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cameraOn, setCameraOn] = useState(true);

  //  front / back camera toggle can be added if needed
  const [facingMode, setFacingMode] = useState("user"); // default front camera
  const toggleCamera = () => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  };

  // Load pre-existing image if provided
  useEffect(() => {
    if (open) {
      if (imageBase64) {
        setCapturedImage(imageBase64);
        setCameraOn(false);
      } else {
        setCapturedImage(null);
        setCameraOn(true);
      }
      setSelectedFile(null);
    }
  }, [open, imageBase64]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const compressedImg = await compressImage(reader.result, 0.7);
        setCapturedImage(compressedImg);
        setSelectedFile(compressedImg);
        setCameraOn(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const captureImage = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    const compressedImg = await compressImage(imageSrc, 0.7);
    setCapturedImage(compressedImg);
    setSelectedFile(null);
    setCameraOn(false);
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setSelectedFile(null);
    setCameraOn(true);
  };

  const handleSave = () => {
    if (capturedImage || selectedFile) {
      onSave(capturedImage || selectedFile);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Upload / Capture Image
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {cameraOn ? (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              style={{ borderRadius: 8 }}
              videoConstraints={{
                facingMode: facingMode,
              }}
            />
            <Button
              // variant="contained"
              onClick={toggleCamera}
              startIcon={
                <img
                  src={facingMode === "user" ? cameraIcon : cameraIcon}
                  alt="camera icon"
                  style={{ width: 30, height: 30 }}
                />
              }
              style={{ marginTop: 10 }}
            ></Button>
          </>
        ) : capturedImage ? (
          <img
            src={capturedImage}
            alt="Preview"
            style={{ width: "100%", borderRadius: 8 }}
          />
        ) : null}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {cameraOn ? (
            <>
              {/* Capture Button */}
              <Grid item xs={6}>
                {/* Desktop View (text + icon) */}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={captureImage}
                  startIcon={<PhotoCameraIcon />}
                  sx={{ display: { xs: "none", sm: "flex" } }}
                >
                  Capture
                </Button>

                {/* Mobile View (icon only) */}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={captureImage}
                  sx={{
                    display: { xs: "flex", sm: "none" },
                    justifyContent: "center",
                  }}
                >
                  <PhotoCameraIcon />
                </Button>
              </Grid>

              {/* Upload Button */}
              <Grid item xs={6}>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  id="fileInput"
                  onChange={handleFileUpload}
                />
                <label htmlFor="fileInput" style={{ width: "100%" }}>
                  {/* Desktop View */}
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ display: { xs: "none", sm: "flex" } }}
                  >
                    Upload
                  </Button>

                  {/* Mobile View */}
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    component="span"
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      justifyContent: "center",
                    }}
                  >
                    <CloudUploadIcon />
                  </Button>
                </label>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleRetake}
              >
                Retake / Re-upload
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="success"
          disabled={!capturedImage && !selectedFile}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebcamDialogStudent;
